import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Form, Input, Alert, App as AntApp } from 'antd';
import { grantSudo } from '@/services/session';
import apiClient from '@/services/api';

/**
 * 共用 sudo gate：呼叫 `require(() => action())` 時若 session 已 sudo 就直接執行 action，
 * 否則跳 password modal，驗證後再執行。
 */
export function useSudoGate(): {
  sudoActive: boolean;
  require: (action: () => void) => void;
  modal: ReactNode;
  openManual: () => void;
} {
  const { message } = AntApp.useApp();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<null | (() => void)>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const r = await apiClient.get<{ sudoActive: boolean }>('/auth/session-me');
      return ((r as any).data ?? r) as { sudoActive: boolean };
    },
    staleTime: 30 * 1000,
  });
  const sudoActive = !!meQuery.data?.sudoActive;

  const mut = useMutation({
    mutationFn: (pw: string) => grantSudo(pw),
    onSuccess: () => {
      message.success('Sudo granted (15 min)');
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      setOpen(false);
      form.resetFields();
      const run = pending;
      setPending(null);
      if (run) setTimeout(run, 50);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Sudo failed');
    },
  });

  const require = (action: () => void) => {
    if (sudoActive) return action();
    setPending(() => action);
    setOpen(true);
  };

  const modal = (
    <Modal
      title="Enter sudo"
      open={open}
      onCancel={() => {
        setOpen(false);
        setPending(null);
        form.resetFields();
      }}
      onOk={() => form.submit()}
      confirmLoading={mut.isPending}
      okText="Grant"
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Re-enter your password. Valid 15 minutes."
      />
      <Form form={form} layout="vertical" onFinish={(v) => mut.mutate(v.password)}>
        <Form.Item name="password" label="Password" rules={[{ required: true }]}>
          <Input.Password autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );

  return { sudoActive, require, modal, openManual: () => setOpen(true) };
}
